import { useEntityList } from '@/shared/hooks/useEntityList';
import type { User } from '@/shared/types/user';
import type { RootState } from '@/store';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UserFilters } from '../store/entitySlice';
import {
  deleteUser,
  fetchUsers,
  setFilters,
  setPage,
  setPerPage,
  USERS_DEFAULT_FILTERS,
} from '../store/entitySlice';

export const useUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const result = useEntityList<User, UserFilters>({
    selector: (state: RootState) => state.usersEntity,
    fetchAction: fetchUsers,
    deleteAction: deleteUser,
    setPageAction: setPage,
    setPerPageAction: setPerPage,
    setFiltersAction: setFilters,
    defaultFilters: USERS_DEFAULT_FILTERS,
    entityName: 'User',
  });

  const { handleFiltersChange } = result;
  const roleFromLink = searchParams.get('role');

  useEffect(() => {
    if (roleFromLink === null) return;
    handleFiltersChange({ ...USERS_DEFAULT_FILTERS, role: roleFromLink });
    const remainingParams = new URLSearchParams(searchParams);
    remainingParams.delete('role');
    setSearchParams(remainingParams, { replace: true });
  }, [roleFromLink, searchParams, setSearchParams, handleFiltersChange]);

  return {
    users: result.items,
    loading: result.loading,
    error: result.error,
    currentPage: result.currentPage,
    perPage: result.perPage,
    total: result.total,
    lastPage: result.lastPage,
    filters: result.filters,
    handleDelete: result.handleDelete,
    handlePageChange: result.handlePageChange,
    handlePerPageChange: result.handlePerPageChange,
    handleFiltersChange: result.handleFiltersChange,
    handleClearFilters: result.handleClearFilters,
  };
};
